// components/Survey/SurveyForm.jsx — renders a survey's questions for answering, preview or read-only review
import React, { useState } from "react";
import { Text } from "@fluentui/react-components";
import { Star24Filled, Star24Regular, CheckmarkCircle20Filled, ErrorCircle20Regular } from "@fluentui/react-icons";
import { OTHER_ID, isVisible, scaleRange } from "./surveyUtils";
import { C } from "./surveyUI";

const inputStyle = (readOnly) => ({
  width: "100%", boxSizing: "border-box", padding: "10px 12px", fontSize: 14,
  border: `1px solid ${C.line}`, borderRadius: 8, outline: "none", fontFamily: "inherit",
  background: readOnly ? C.soft : "#fff", color: C.ink,
});

function focusRing(e, on) {
  e.target.style.borderColor = on ? C.accent : C.line;
  e.target.style.boxShadow = on ? "0 0 0 3px rgba(0,102,255,0.12)" : "none";
}

function OptionTile({ selected, onClick, readOnly, multi, children }) {
  return (
    <button
      type="button"
      role={multi ? "checkbox" : "radio"}
      aria-checked={selected}
      disabled={readOnly}
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
        padding: "11px 14px", borderRadius: 10, fontSize: 14, fontFamily: "inherit",
        border: `1.5px solid ${selected ? C.accent : C.line}`,
        background: selected ? "#F0F6FF" : "#fff", color: C.ink,
        cursor: readOnly ? "default" : "pointer", transition: "all 0.12s",
      }}
    >
      <span style={{
        width: 18, height: 18, flexShrink: 0, borderRadius: multi ? 5 : "50%",
        border: `2px solid ${selected ? C.accent : "#B5B3B0"}`,
        background: selected ? C.accent : "#fff",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>
        {selected && (multi
          ? <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6.5l2.5 2.5L10 3.5" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
          : <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />)}
      </span>
      <span style={{ flex: 1 }}>{children}</span>
    </button>
  );
}

function ChoiceInput({ q, value, answers, onChange, readOnly, multi }) {
  const selected = multi ? (Array.isArray(value) ? value : []) : value;
  const otherKey = `${q.id}${OTHER_ID}`;
  const toggle = (id) => {
    if (readOnly) return;
    if (multi) onChange(q.id, selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    else onChange(q.id, selected === id ? "" : id);
  };
  const isSel = (id) => (multi ? selected.includes(id) : selected === id);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} role={multi ? "group" : "radiogroup"}>
      {(q.options || []).map((o) => (
        <OptionTile key={o.id} selected={isSel(o.id)} onClick={() => toggle(o.id)} readOnly={readOnly} multi={multi}>{o.label}</OptionTile>
      ))}
      {q.allowOther && (
        <>
          <OptionTile selected={isSel(OTHER_ID)} onClick={() => toggle(OTHER_ID)} readOnly={readOnly} multi={multi}>Other</OptionTile>
          {isSel(OTHER_ID) && (
            <input
              value={answers[otherKey] || ""}
              onChange={(e) => onChange(otherKey, e.target.value)}
              readOnly={readOnly}
              placeholder="Please specify"
              style={{ ...inputStyle(readOnly), marginLeft: 30, width: "calc(100% - 30px)" }}
              onFocus={(e) => focusRing(e, true)}
              onBlur={(e) => focusRing(e, false)}
            />
          )}
        </>
      )}
    </div>
  );
}

function StarRating({ q, value, onChange, readOnly }) {
  const [hover, setHover] = useState(0);
  const max = Number(q.ratingMax) || 5;
  const shown = hover || Number(value) || 0;
  const labels = ["", "Very poor", "Poor", "Average", "Good", "Excellent"];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
      <div style={{ display: "flex", gap: 4 }} role="radiogroup" onMouseLeave={() => setHover(0)}>
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
          const on = n <= shown;
          const Icon = on ? Star24Filled : Star24Regular;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={Number(value) === n}
              aria-label={`${n} of ${max}`}
              disabled={readOnly}
              onClick={() => onChange(q.id, Number(value) === n ? "" : n)}
              onMouseEnter={() => !readOnly && setHover(n)}
              style={{ background: "none", border: "none", padding: 2, cursor: readOnly ? "default" : "pointer", color: on ? "#E8A400" : "#C8C6C4", transform: hover === n ? "scale(1.15)" : "none", transition: "transform 0.1s" }}
            >
              <Icon style={{ fontSize: max > 5 ? 26 : 34 }} />
            </button>
          );
        })}
      </div>
      {shown > 0 && (
        <Text style={{ fontSize: 13, fontWeight: 600, color: C.ink2 }}>
          {max === 5 ? labels[shown] : `${shown} / ${max}`}
        </Text>
      )}
    </div>
  );
}

function NumberScale({ q, value, onChange, readOnly }) {
  const [min, max] = scaleRange(q);
  const points = [];
  for (let i = min; i <= max; i++) points.push(i);
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${points.length}, minmax(36px, 1fr))`, gap: 6, overflowX: "auto" }} role="radiogroup">
        {points.map((n) => {
          const sel = Number(value) === n && value !== "";
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={sel}
              disabled={readOnly}
              onClick={() => onChange(q.id, sel ? "" : n)}
              style={{
                height: 44, borderRadius: 8, fontSize: 15, fontWeight: 700, fontFamily: "inherit",
                border: `1.5px solid ${sel ? C.accent : C.line}`,
                background: sel ? C.accent : "#fff", color: sel ? "#fff" : C.ink,
                cursor: readOnly ? "default" : "pointer", transition: "all 0.12s",
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      {(q.minLabel || q.maxLabel) && (
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 12, color: C.muted, gap: 12 }}>
          <span>{q.minLabel}</span>
          <span style={{ textAlign: "right" }}>{q.maxLabel}</span>
        </div>
      )}
    </div>
  );
}

function YesNo({ q, value, onChange, readOnly }) {
  return (
    <div style={{ display: "flex", gap: 10 }} role="radiogroup">
      {[["yes", "Yes"], ["no", "No"]].map(([v, label]) => {
        const sel = value === v;
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={sel}
            disabled={readOnly}
            onClick={() => onChange(q.id, sel ? "" : v)}
            style={{
              minWidth: 110, padding: "11px 22px", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: "inherit",
              border: `1.5px solid ${sel ? C.accent : C.line}`,
              background: sel ? C.accent : "#fff", color: sel ? "#fff" : C.ink,
              cursor: readOnly ? "default" : "pointer",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function Likert({ q, value, onChange, readOnly }) {
  const v = value && typeof value === "object" ? value : {};
  const cols = q.columns || [];
  const set = (rowId, colId) => {
    if (readOnly) return;
    const next = { ...v };
    if (next[rowId] === colId) delete next[rowId];
    else next[rowId] = colId;
    onChange(q.id, next);
  };
  return (
    <div style={{ overflowX: "auto", margin: "0 -4px" }}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "4px 4px", minWidth: 120 + cols.length * 84 }}>
        <thead>
          <tr>
            <th />
            {cols.map((c) => (
              <th key={c.id} style={{ fontSize: 11, fontWeight: 600, color: C.ink2, padding: "4px 2px", textAlign: "center", lineHeight: 1.3, width: `${60 / cols.length}%` }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(q.rows || []).map((r, ri) => (
            <tr key={r.id} role="radiogroup" aria-label={r.label}>
              <td style={{ fontSize: 14, color: C.ink, padding: "10px 12px", background: ri % 2 ? "#fff" : C.soft, borderRadius: 8, lineHeight: 1.4 }}>
                {r.label}
                {!v[r.id] && !readOnly && <span style={{ color: C.muted }} />}
              </td>
              {cols.map((c) => {
                const sel = v[r.id] === c.id;
                return (
                  <td key={c.id} style={{ textAlign: "center", background: ri % 2 ? "#fff" : C.soft, borderRadius: 8 }}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={sel}
                      aria-label={c.label}
                      disabled={readOnly}
                      onClick={() => set(r.id, c.id)}
                      style={{
                        width: 22, height: 22, borderRadius: "50%", padding: 0,
                        border: `2px solid ${sel ? C.accent : "#B5B3B0"}`,
                        background: "#fff", cursor: readOnly ? "default" : "pointer",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      {sel && <span style={{ width: 10, height: 10, borderRadius: "50%", background: C.accent }} />}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QuestionInput({ q, answers, onChange, readOnly }) {
  const value = answers[q.id];
  switch (q.type) {
    case "short_text":
      return <input value={value || ""} onChange={(e) => onChange(q.id, e.target.value)} readOnly={readOnly} placeholder={readOnly ? "" : "Your answer"} maxLength={500} style={inputStyle(readOnly)} onFocus={(e) => focusRing(e, true)} onBlur={(e) => focusRing(e, false)} />;
    case "long_text":
      return <textarea value={value || ""} onChange={(e) => onChange(q.id, e.target.value)} readOnly={readOnly} placeholder={readOnly ? "" : "Your answer"} rows={4} maxLength={4000} style={{ ...inputStyle(readOnly), resize: "vertical", lineHeight: 1.6 }} onFocus={(e) => focusRing(e, true)} onBlur={(e) => focusRing(e, false)} />;
    case "single_choice":
      return <ChoiceInput q={q} value={value} answers={answers} onChange={onChange} readOnly={readOnly} />;
    case "multi_choice":
      return <ChoiceInput q={q} value={value} answers={answers} onChange={onChange} readOnly={readOnly} multi />;
    case "dropdown":
      return (
        <div style={{ maxWidth: 420 }}>
          <select value={value || ""} onChange={(e) => onChange(q.id, e.target.value)} disabled={readOnly} style={{ ...inputStyle(readOnly), cursor: readOnly ? "default" : "pointer" }}>
            <option value="">Select an option…</option>
            {(q.options || []).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            {q.allowOther && <option value={OTHER_ID}>Other</option>}
          </select>
          {value === OTHER_ID && (
            <input value={answers[`${q.id}${OTHER_ID}`] || ""} onChange={(e) => onChange(`${q.id}${OTHER_ID}`, e.target.value)} readOnly={readOnly} placeholder="Please specify" style={{ ...inputStyle(readOnly), marginTop: 8 }} />
          )}
        </div>
      );
    case "yes_no":
      return <YesNo q={q} value={value} onChange={onChange} readOnly={readOnly} />;
    case "rating":
      return <StarRating q={q} value={value} onChange={onChange} readOnly={readOnly} />;
    case "scale":
    case "nps":
      return <NumberScale q={q} value={value} onChange={onChange} readOnly={readOnly} />;
    case "likert":
      return <Likert q={q} value={value} onChange={onChange} readOnly={readOnly} />;
    case "number":
      return <input type="number" value={value ?? ""} onChange={(e) => onChange(q.id, e.target.value === "" ? "" : e.target.value)} readOnly={readOnly} min={q.min === "" ? undefined : q.min} max={q.max === "" ? undefined : q.max} placeholder={readOnly ? "" : "Enter a number"} style={{ ...inputStyle(readOnly), maxWidth: 240 }} onFocus={(e) => focusRing(e, true)} onBlur={(e) => focusRing(e, false)} />;
    case "date":
      return <input type="date" value={value || ""} onChange={(e) => onChange(q.id, e.target.value)} readOnly={readOnly} style={{ ...inputStyle(readOnly), maxWidth: 240 }} />;
    default:
      return null;
  }
}

/**
 * Props:
 *   survey     { questions }
 *   answers    { [questionId]: value }
 *   onChange   (key, value) => void
 *   errors     { [questionId]: message }
 *   readOnly   boolean
 */
export default function SurveyForm({ survey, answers, onChange, errors = {}, readOnly = false }) {
  let number = 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {survey.questions.map((q) => {
        if (!isVisible(q, answers)) return null;
        if (q.type === "section") {
          return (
            <div key={q.id} style={{ padding: "18px 4px 2px" }}>
              <Text style={{ fontSize: 18, fontWeight: 700, color: C.navy, display: "block" }}>{q.title || "Untitled section"}</Text>
              {q.description && <Text style={{ fontSize: 13, color: C.ink2, display: "block", marginTop: 4, lineHeight: 1.6 }}>{q.description}</Text>}
              <div style={{ height: 3, width: 40, background: C.accent, borderRadius: 2, marginTop: 10 }} />
            </div>
          );
        }
        number++;
        const err = errors[q.id];
        const answered = answers[q.id] !== undefined && answers[q.id] !== "" && !(Array.isArray(answers[q.id]) && !answers[q.id].length);
        return (
          <div
            key={q.id}
            id={`sq-${q.id}`}
            style={{
              background: "#fff", borderRadius: 12, padding: "20px 22px",
              border: `1px solid ${err ? "#F1BBBC" : C.line}`,
              boxShadow: err ? "0 0 0 3px rgba(209,52,56,0.08)" : "0 1px 3px rgba(0,26,61,0.04)",
              borderLeft: `4px solid ${err ? C.danger : answered ? "#4F9D4F" : "transparent"}`,
              transition: "border-color 0.2s",
            }}
          >
            <div style={{ display: "flex", gap: 10, marginBottom: q.description ? 4 : 14 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.accent, minWidth: 22, paddingTop: 1 }}>{number}.</span>
              <Text style={{ fontSize: 15, fontWeight: 600, color: C.ink, lineHeight: 1.45, flex: 1 }}>
                {q.title || <span style={{ color: C.muted }}>Untitled question</span>}
                {q.required && <span style={{ color: C.danger }} aria-label="required"> *</span>}
              </Text>
              {answered && !readOnly && <CheckmarkCircle20Filled style={{ color: "#4F9D4F", flexShrink: 0 }} />}
            </div>
            {q.description && <Text style={{ display: "block", fontSize: 13, color: C.ink2, margin: "0 0 14px 32px", lineHeight: 1.6 }}>{q.description}</Text>}
            <div style={{ marginLeft: 32 }}>
              <QuestionInput q={q} answers={answers} onChange={onChange} readOnly={readOnly} />
              {readOnly && !answered && <Text style={{ fontSize: 13, color: C.muted, fontStyle: "italic" }}>No answer</Text>}
              {err && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: C.danger, fontSize: 12, fontWeight: 600, marginTop: 10 }}>
                  <ErrorCircle20Regular style={{ fontSize: 16 }} /> {err}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
